import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { morph } from '@theme/morph';

// Tempo que a mensagem de erro fica visivel, alinhado com product-form.js
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

/**
 * Grade de quantidade por tamanho.
 *
 * Recebe uma quantidade por tamanho da cor selecionada e envia todos os
 * tamanhos ao carrinho numa unica requisicao, no mesmo formato usado pelo
 * batch add to cart do tema (items[] + section rendering).
 */
class GradeQuantidadeComponent extends HTMLElement {
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #errorTimeout;

  connectedCallback() {
    const { signal } = this.#abortController;

    this.addEventListener('input', this.#handleInput, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    this.addEventListener('click', this.#handleClick, { signal });

    this.closest('.shopify-section, dialog')?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal }
    );

    this.#refresh();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    clearTimeout(this.#errorTimeout);
  }

  /** @returns {HTMLInputElement[]} */
  get #inputs() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('[data-grade-input]')]);
  }

  /**
   * Quantidade valida de um campo. Campo vazio ou invalido conta como zero.
   * @param {HTMLInputElement} input
   * @returns {number}
   */
  #quantidade(input) {
    const valor = parseInt(input.value, 10);
    if (!Number.isFinite(valor) || valor < 0) return 0;

    const max = parseInt(input.max, 10);
    if (Number.isFinite(max) && valor > max) return max;

    return valor;
  }

  /** @param {Event} event */
  #handleInput = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-grade-input]')) return;
    this.#refresh();
  };

  /**
   * Normaliza o campo quando ele perde o foco, para nao atrapalhar a digitacao.
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-grade-input]')) return;

    const quantidade = this.#quantidade(event.target);
    event.target.value = quantidade === 0 ? '' : String(quantidade);
    this.#refresh();
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-grade-submit]')) {
      event.preventDefault();
      this.#adicionarAoCarrinho();
      return;
    }

    if (event.target.closest('[data-grade-limpar]')) {
      event.preventDefault();
      this.#limpar();
    }
  };

  /**
   * Ao trocar de cor o tema devolve o HTML novo da secao. Trocamos apenas o
   * corpo da grade, porque os tamanhos disponiveis mudam de uma cor para outra.
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail?.data?.productId !== this.dataset.productId) return;

    const seletor = `grade-quantidade-component[data-block-id="${this.dataset.blockId}"] [data-grade-body]`;
    const novoCorpo = event.detail.data.html?.querySelector(seletor);
    const corpoAtual = this.querySelector('[data-grade-body]');

    if (novoCorpo && corpoAtual) morph(corpoAtual, novoCorpo);

    this.#limparErro();
    this.#refresh();
  };

  /** Recalcula totais e o estado do botao. */
  #refresh() {
    let pecas = 0;
    let centavos = 0;

    for (const input of this.#inputs) {
      const quantidade = this.#quantidade(input);
      pecas += quantidade;
      centavos += quantidade * Number(input.dataset.price || 0);
    }

    const totalPecas = this.querySelector('[data-grade-total-pecas]');
    const totalValor = this.querySelector('[data-grade-total-valor]');
    const submit = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-grade-submit]'));
    const limpar = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-grade-limpar]'));

    if (totalPecas) {
      const rotulo = pecas === 1 ? this.dataset.labelPeca : this.dataset.labelPecas;
      totalPecas.textContent = `${pecas} ${rotulo}`;
    }
    if (totalValor) totalValor.textContent = this.#formatarDinheiro(centavos);
    if (submit) submit.disabled = pecas === 0;
    if (limpar) limpar.hidden = pecas === 0;
  }

  /**
   * @param {number} centavos
   * @returns {string}
   */
  #formatarDinheiro(centavos) {
    const locale = this.dataset.locale || 'pt-BR';
    const currency = this.dataset.currency || 'BRL';

    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(centavos / 100);
    } catch {
      return (centavos / 100).toFixed(2);
    }
  }

  #limpar() {
    for (const input of this.#inputs) input.value = '';
    this.#limparErro();
    this.#refresh();
  }

  async #adicionarAoCarrinho() {
    const items = this.#inputs
      .map((input) => ({ id: Number(input.dataset.variantId), quantity: this.#quantidade(input) }))
      .filter((item) => item.quantity > 0 && Number.isFinite(item.id));

    if (items.length === 0) return;

    const submit = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-grade-submit]'));
    this.#limparErro();
    this.setAttribute('aria-busy', 'true');
    if (submit) submit.disabled = true;

    // Mesmas secoes que o product-form atualiza, para o drawer refletir o carrinho novo.
    const sectionIds = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });

      const data = await response.json();

      if (data.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, data.message, data.description, data.errors));
        this.#mostrarErro(data.description || data.message);
        return;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
      const totalPecas = items.reduce((soma, item) => soma + item.quantity, 0);

      this.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'grade-quantidade-component',
          itemCount: totalPecas,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );

      this.#anunciar(`${totalPecas} ${totalPecas === 1 ? this.dataset.labelPeca : this.dataset.labelPecas}`);
      this.#limpar();
    } catch (error) {
      console.error(error);
      this.#mostrarErro(this.dataset.labelErro || '');
    } finally {
      this.removeAttribute('aria-busy');
      this.#refresh();
    }
  }

  /** @param {string} mensagem */
  #mostrarErro(mensagem) {
    const alvo = this.querySelector('[data-grade-error]');
    if (!alvo || !mensagem) return;

    alvo.textContent = mensagem;
    alvo.hidden = false;

    clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => this.#limparErro(), ERROR_MESSAGE_DISPLAY_DURATION);
  }

  #limparErro() {
    const alvo = this.querySelector('[data-grade-error]');
    if (!alvo) return;

    alvo.textContent = '';
    alvo.hidden = true;
  }

  /** @param {string} mensagem */
  #anunciar(mensagem) {
    const liveRegion = this.querySelector('[data-grade-live]');
    if (!liveRegion) return;

    liveRegion.textContent = `${Theme.translations.added}: ${mensagem}`;
    setTimeout(() => {
      liveRegion.textContent = '';
    }, 5000);
  }
}

if (!customElements.get('grade-quantidade-component')) {
  customElements.define('grade-quantidade-component', GradeQuantidadeComponent);
}
